import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { gql } from '../lib/api';
import {
  COST_ENTRIES_QUERY,
  MONTHLY_SUMMARY_QUERY,
  STORES_QUERY,
} from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';
import {
  currentYearMonth,
  extractYearMonthFromPeriodKey,
  recentYearMonthRange,
} from '../lib/yearMonth';

interface Store {
  id: string;
  name: string;
}

interface CostEntry {
  storeId: string;
  date: string;
  costId: string;
  category: string;
  itemName: string;
  costType: string | null;
  reportInput: string | null;
  amount: number;
}

interface MonthSummary {
  storeId: string;
  periodKey: string;
  sales: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  原価: '#d6006c',
  人件費: '#edbb00',
  広告宣伝費: '#6b6379',
  地代家賃: '#0088b0',
  '備品・消耗品費': '#15aabf',
  その他: '#868e96',
};

const colorFor = (cat: string): string => CATEGORY_COLORS[cat] ?? '#adb5bd';

const dateRangeForMonth = (yearMonth: string): { from: string; to: string } => {
  const [y, m] = yearMonth.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    from: `${yearMonth}-01`,
    to: `${yearMonth}-${String(lastDay).padStart(2, '0')}`,
  };
};

export const FoodPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const storeParam = searchParams.get('store');
  const ymParam = searchParams.get('ym');
  const initYearMonth = /^\d{4}-\d{2}$/.test(ymParam ?? '') ? (ymParam as string) : '';

  const [storeId, setStoreId] = useState<string>(storeParam || storedStoreId);
  const [yearMonth, setYearMonth] = useState(initYearMonth);
  const [monthInitialized, setMonthInitialized] = useState(!!initYearMonth);

  // store 変更を localStorage にも反映（他ページとの整合性）
  useEffect(() => {
    if (storeId) setStoredStoreId(storeId);
  }, [storeId, setStoredStoreId]);

  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];
  const effectiveStoreId = stores.find((s) => s.id === storeId)?.id ?? stores[0]?.id ?? '';

  // state 変更を URL に反映
  useEffect(() => {
    const next = new URLSearchParams();
    if (effectiveStoreId) next.set('store', effectiveStoreId);
    if (yearMonth) next.set('ym', yearMonth);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [effectiveStoreId, yearMonth, searchParams, setSearchParams]);
  const { fromYearMonth: probeFromYearMonth, toYearMonth: probeToYearMonth } =
    useMemo(() => recentYearMonthRange(12), []);

  const recentMonthlyQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['monthlyForCostsProbe', effectiveStoreId, probeFromYearMonth, probeToYearMonth],
    queryFn: () =>
      gql<{ monthlySummary: Pick<MonthSummary, 'periodKey'>[] }>(MONTHLY_SUMMARY_QUERY, {
        storeId: effectiveStoreId,
        fromYearMonth: probeFromYearMonth,
        toYearMonth: probeToYearMonth,
      }),
  });

  const latestAvailableMonth =
    recentMonthlyQ.data?.monthlySummary
      ?.map((summary) => extractYearMonthFromPeriodKey(summary.periodKey))
      .sort()
      .at(-1) ?? currentYearMonth();

  useEffect(() => {
    setMonthInitialized(false);
    setYearMonth('');
  }, [effectiveStoreId]);

  useEffect(() => {
    if (!effectiveStoreId || monthInitialized || recentMonthlyQ.isLoading) return;
    setYearMonth(latestAvailableMonth);
    setMonthInitialized(true);
  }, [effectiveStoreId, latestAvailableMonth, monthInitialized, recentMonthlyQ.isLoading]);

  const { from, to } = yearMonth
    ? dateRangeForMonth(yearMonth)
    : { from: '', to: '' };

  const costsQ = useQuery({
    enabled: !!effectiveStoreId && !!yearMonth && monthInitialized,
    queryKey: ['costEntries', effectiveStoreId, from, to],
    queryFn: () =>
      gql<{ costEntries: CostEntry[] }>(COST_ENTRIES_QUERY, {
        storeId: effectiveStoreId,
        from,
        to,
      }),
  });

  const monthlyQ = useQuery({
    enabled: !!effectiveStoreId && !!yearMonth && monthInitialized,
    queryKey: ['monthlyForCosts', effectiveStoreId, yearMonth],
    queryFn: () =>
      gql<{ monthlySummary: MonthSummary[] }>(MONTHLY_SUMMARY_QUERY, {
        storeId: effectiveStoreId,
        fromYearMonth: yearMonth,
        toYearMonth: yearMonth,
      }),
  });

  const allCosts = costsQ.data?.costEntries ?? [];
  // Airメイト「日次コスト明細」は reportInput=あり のみ採用。
  // 月次コスト管理の日割り（家賃・通信費・広告等）と Airシフト連携の人件費はここでは扱わない。
  //   - 家賃・固定費: 店舗マスタ／固定費ページで直接入力
  //   - 人件費: 人件費ページ（/labor）で Airシフト × スタッフマスタから別途算出
  const costs = useMemo(
    () => allCosts.filter((c) => c.reportInput === 'あり' && c.category !== '人件費'),
    [allCosts],
  );
  const monthSales = monthlyQ.data?.monthlySummary[0]?.sales ?? 0;

  const totals = useMemo(() => {
    const byCategory: Record<string, number> = {};
    let grand = 0;
    let foodCost = 0;
    let fixed = 0;
    let variable = 0;
    for (const c of costs) {
      byCategory[c.category] = (byCategory[c.category] ?? 0) + c.amount;
      grand += c.amount;
      if (c.category === '原価') foodCost += c.amount;
      if (c.costType === '固定費') fixed += c.amount;
      if (c.costType === '変動費') variable += c.amount;
    }
    return { byCategory, grand, foodCost, fixed, variable };
  }, [costs]);

  const grouped = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const c of costs) {
      const cur = map.get(c.date) ?? {};
      cur[c.category] = (cur[c.category] ?? 0) + c.amount;
      map.set(c.date, cur);
    }
    const categories = Array.from(new Set(costs.map((c) => c.category)));
    const labels = Array.from(map.keys()).sort();
    return labels.map((label) => {
      const row: Record<string, number | string> = { label };
      for (const cat of categories) row[cat] = map.get(label)?.[cat] ?? 0;
      return row;
    });
  }, [costs]);

  const chartCategories = useMemo(
    () => Array.from(new Set(costs.map((c) => c.category))),
    [costs],
  );

  const displayItemName = (c: { category: string; itemName: string }): string =>
    c.category === '人件費' && c.itemName === 'Airシフト連携'
      ? 'アルバイト人件費（Airシフト連携）'
      : c.itemName;

  const itemBreakdown = useMemo(() => {
    const map = new Map<string, { category: string; itemName: string; amount: number; days: Set<string> }>();
    for (const c of costs) {
      const display = displayItemName(c);
      const key = `${c.category}::${display}`;
      const cur = map.get(key) ?? {
        category: c.category,
        itemName: display,
        amount: 0,
        days: new Set<string>(),
      };
      cur.amount += c.amount;
      cur.days.add(c.date);
      map.set(key, cur);
    }
    return Array.from(map.values())
      .map((v) => ({ ...v, dayCount: v.days.size }))
      .sort((a, b) => b.amount - a.amount);
  }, [costs]);

  const foodCostRate = monthSales > 0 ? (totals.foodCost / monthSales) * 100 : null;

  return (
    <div>
      <div style={pageHeader}>
        <h1 style={pageTitle}>食材費</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={effectiveStoreId}
            onChange={(e) => setStoreId(e.target.value)}
            style={selectStyle}
          >
            {stores.length === 0 && <option value="">（店舗マスタ未登録）</option>}
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="month"
            value={yearMonth}
            onChange={(e) => {
              setYearMonth(e.target.value);
              setMonthInitialized(true);
            }}
            style={selectStyle}
          />
        </div>
      </div>

      <section style={notice}>
        <strong>表示中: 日次仕入れ（日報入力=あり）</strong>
        <div style={{ fontSize: '0.78rem', color: '#7a4a00', marginTop: 4 }}>
          このページは<strong>食材費（原価）の内訳分析</strong>専用です。Airメイト「月次コスト管理」の日割りデータと「Airシフト連携」由来の人件費は除外しています。<br />
          • 人件費 → <strong>人件費ページ（/labor）</strong>で Airシフト × スタッフマスタの時給／月給から直接算出<br />
          • 家賃・固定費 → 店舗マスタ／固定費ページで直接入力
        </div>
      </section>

      <section style={kpiGrid}>
        <KpiCard label="今月売上" value={fmtYen(monthSales)} />
        <KpiCard label="食材費合計" value={fmtYen(totals.foodCost)} />
        <KpiCard
          label="食材費率（F）"
          value={foodCostRate !== null ? `${foodCostRate.toFixed(1)}%` : '—'}
        />
      </section>

      <section style={cardSection}>
        <h2 style={sectionTitle}>コスト推移（日次、コスト分類別 積上げ）</h2>
        {recentMonthlyQ.isLoading || costsQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : recentMonthlyQ.error || costsQ.error || monthlyQ.error ? (
          <p style={muted}>
            データ取得に失敗しました。
            {'message' in (recentMonthlyQ.error ?? costsQ.error ?? monthlyQ.error ?? {})
              ? ` ${(recentMonthlyQ.error ?? costsQ.error ?? monthlyQ.error)?.message ?? ''}`
              : ''}
          </p>
        ) : grouped.length === 0 ? (
          <p style={muted}>
            データがまだありません。Airメイトの「日次コスト明細」CSV をアップロードしてください。
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={grouped}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`} />
              <Tooltip
                formatter={(value) => [
                  `¥${Math.round(Number(value)).toLocaleString()}`,
                  '',
                ]}
              />
              <Legend />
              {chartCategories.map((cat) => (
                <Bar key={cat} dataKey={cat} stackId="a" fill={colorFor(cat)} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

      <section style={cardSection}>
        <h2 style={sectionTitle}>分類別 内訳（当月合計）</h2>
        {Object.keys(totals.byCategory).length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>分類</th>
                <th style={{ ...th, textAlign: 'right' }}>金額</th>
                <th style={{ ...th, textAlign: 'right' }}>構成比</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(totals.byCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, amt]) => (
                  <tr key={cat}>
                    <td style={td}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: colorFor(cat),
                          marginRight: 6,
                        }}
                      />
                      {cat}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      ¥{amt.toLocaleString()}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#6c757d' }}>
                      {totals.grand > 0 ? `${((amt / totals.grand) * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={cardSection}>
        <h2 style={sectionTitle}>項目別 詳細（当月）</h2>
        {itemBreakdown.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>分類</th>
                <th style={th}>項目</th>
                <th style={{ ...th, textAlign: 'right' }}>計上日数</th>
                <th style={{ ...th, textAlign: 'right' }}>金額</th>
              </tr>
            </thead>
            <tbody>
              {itemBreakdown.map((r) => (
                <tr key={`${r.category}-${r.itemName}`}>
                  <td style={td}>
                    <span style={{ color: colorFor(r.category) }}>●</span> {r.category}
                  </td>
                  <td style={td}>{r.itemName}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#6c757d' }}>
                    {r.dayCount}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    ¥{r.amount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: string;
}
const KpiCard = ({ label, value }: KpiCardProps) => (
  <div style={kpiCardStyle}>
    <div style={kpiLabel}>{label}</div>
    <div style={kpiValue}>{value}</div>
  </div>
);

const fmtYen = (n: number | null | undefined): string =>
  typeof n === 'number' ? `¥${Math.round(n).toLocaleString()}` : '—';

const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '0.5rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
};
const kpiGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
};
const kpiCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.9rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
};
const kpiLabel: React.CSSProperties = { color: '#6c757d', fontSize: '0.8rem' };
const kpiValue: React.CSSProperties = { fontSize: '1.4rem', fontWeight: 700, marginTop: 4 };
const cardSection: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const notice: React.CSSProperties = {
  background: '#fff8e1',
  border: '1px solid #ffe082',
  color: '#7a4a00',
  borderRadius: 8,
  padding: '0.7rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
